import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import { Button, Spinner, Alert } from 'reactstrap'
import moment from 'moment'
import { notifyError, notifySuccess } from '../../../utils/notify'
import {
  FICHA_PJ_STATUS,
  fetchFichasPj,
  updateFichaPjStatus,
} from './fichaPjApi'
import FichaPjDetailModal from './FichaPjDetailModal'

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '12px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 25, 50],
  },
  enableDensityToggle: false,
  positionPagination: 'bottom',
}

const statusStyle = (status) => {
  const aprovado = status === FICHA_PJ_STATUS.APROVADO
  return {
    border: `2px solid ${aprovado ? '#34c38f' : '#f46a6a'}`,
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 12,
    minWidth: 150,
    cursor: 'pointer',
    fontWeight: 600,
    color: aprovado ? '#34c38f' : '#f46a6a',
    backgroundColor: aprovado ? '#e8f8f0' : '#fdecea',
  }
}

const RhFichaPjPanel = () => {
  const [fichas, setFichas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedFicha, setSelectedFicha] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const loadFichas = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchFichasPj()
      setFichas(data)
    } catch (err) {
      setError('Não foi possível carregar as fichas cadastrais.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFichas()
  }, [loadFichas])

  const handleStatusChange = async (row, newStatus) => {
    const id = row.fichaPj_id
    try {
      await updateFichaPjStatus(id, newStatus)
      setFichas((prev) => prev.map((f) => (
        f.fichaPj_id === id ? { ...f, fichaPj_status: newStatus } : f
      )))
      notifySuccess('Status atualizado.')
    } catch (err) {
      notifyError('Erro ao atualizar o status.')
    }
  }

  const openDetail = (ficha) => {
    setSelectedFicha(ficha)
    setDetailOpen(true)
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'pf_nomeExibicao',
      header: 'Nome',
      size: 260,
      accessorFn: (row) => row.pf_nomeExibicao || row.fichaPj_repAssinaturaNome || row.fichaPj_razaoSocial || '—',
    },
    {
      accessorKey: 'fichaPj_dtCria',
      header: 'Enviado em',
      size: 150,
      accessorFn: (row) => (row.fichaPj_dtCria ? new Date(row.fichaPj_dtCria).getTime() : 0),
      Cell: ({ row }) => (
        row.original.fichaPj_dtCria
          ? moment(row.original.fichaPj_dtCria).format('DD/MM/YYYY HH:mm')
          : '—'
      ),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'fichaPj_status',
      header: 'Status',
      size: 180,
      Cell: ({ row }) => {
        const status = row.original.fichaPj_status || FICHA_PJ_STATUS.NAO_REVISADO
        return (
          <select
            value={status}
            onChange={(e) => handleStatusChange(row.original, e.target.value)}
            style={statusStyle(status)}
          >
            <option value={FICHA_PJ_STATUS.NAO_REVISADO}>{FICHA_PJ_STATUS.NAO_REVISADO}</option>
            <option value={FICHA_PJ_STATUS.APROVADO}>{FICHA_PJ_STATUS.APROVADO}</option>
          </select>
        )
      },
    },
    {
      accessorKey: 'ver_formulario',
      header: 'Formulário',
      size: 130,
      enableSorting: false,
      Cell: ({ row }) => (
        <Button
          size="sm"
          color="primary"
          outline
          onClick={() => openDetail(row.original)}
        >
          Ver ficha
        </Button>
      ),
    },
  ], [])

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <div className="mt-2">
      {error && <Alert color="danger">{error}</Alert>}

      <p className="text-muted mb-3 small">
        Cadastros de prestadores PJ enviados pelo formulário público.
      </p>

      <MaterialReactTable
        columns={columns}
        data={fichas}
        localization={MRT_Localization_PT}
        initialState={{
          sorting: [{ id: 'fichaPj_dtCria', desc: true }],
          pagination: { pageSize: 10, pageIndex: 0 },
        }}
        muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
        {...mrtCompactProps}
      />

      <FichaPjDetailModal
        isOpen={detailOpen}
        toggle={() => setDetailOpen(false)}
        ficha={selectedFicha}
      />
    </div>
  )
}

export default RhFichaPjPanel
